import React, { useState } from 'react';
import { analytics } from '@economic/react-app';
import { Button, Dialog, DialogProps, Group, Icon } from '@economic/taco';
import { Trans } from '@lingui/macro';

import { useLocation } from 'react-router-dom';
import track from '~/mixpanel-service';
import ExternalLink from '../../../../shared/components/ExternalLink';
import internalApiClient from '../../../../shared/util/api/internalApiClient';
import SubscriptionChangeSuccessDialog from './SubscriptionChangeSuccessDialog';

interface Props {
    isDowngrade: boolean;
    isTrialUpgrade: boolean;
    planName: string;
    planId: number;
    readMoreLink: string | undefined;
    modalProps: Partial<DialogProps>;
    redirectToSubscriptionStatusPage: boolean;
}

export default function ConfirmSubscriptionChangeDialog({
    modalProps,
    isDowngrade,
    isTrialUpgrade,
    planName,
    planId,
    readMoreLink,
    redirectToSubscriptionStatusPage = true,
}: Props) {
    const { state } = useLocation();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    async function selectPlan() {
        if (isTrialUpgrade) {
            await internalApiClient.post(`/company/change-subscription?newPlanId=${planId}&upgradeTrial=true`);
            analytics.gtm('trial upgraded', {
                gaCategory: 'subscription',
                gaAction: 'trial upgraded',
                packageChangeDestination: planName,
                gaLabel: planName,
            });
        } else {
            await internalApiClient.post(`/company/change-subscription?newPlanId=${planId}`);
            if (isDowngrade) {
                analytics.gtm('package downgrade', {
                    gaCategory: 'subscription',
                    gaAction: 'package downgrade',
                    packageChangeDestination: planName,
                    gaLabel: planName,
                });
            } else {
                analytics.gtm('package upgrade', {
                    gaCategory: 'subscription',
                    gaAction: 'package upgrade',
                    packageChangeDestination: planName,
                    gaLabel: planName,
                });
                track('subscriptions', 'plan-upgraded', {
                    origin: state?.origin || '',
                    newPlan: planName,
                });
            }
        }

        setShowSuccessDialog(true);
    }

    if (showSuccessDialog) {
        return (
            <SubscriptionChangeSuccessDialog
                planId={planId}
                isDowngrade={isDowngrade}
                modalProps={modalProps}
                readMoreLink={readMoreLink}
                redirectToSubscriptionStatusPage={redirectToSubscriptionStatusPage}
            />
        );
    }

    return (
        <Dialog {...modalProps} size="xs">
            <Dialog.Content aria-label="">
                <Dialog.Title>
                    {isTrialUpgrade && <Trans>Upgrade to {planName}</Trans>}
                    {isDowngrade && (
                        <>
                            <Trans>You will lose functionality</Trans>{' '}
                            <Icon name="warning" className="text-sm rounded-full yt-yellow-solid ml-2" />
                        </>
                    )}
                    {!isTrialUpgrade && !isDowngrade && <Trans>Confirm subscription change</Trans>}
                </Dialog.Title>
                <>
                    {isDowngrade && (
                        <p>
                            <Trans>The selected package contains less functionality than you currently have available.</Trans>
                        </p>
                    )}
                    {isTrialUpgrade && (
                        <p>
                            <Trans>
                                The upgrade will take effect immediate, and you will be invoiced for your new subscription from
                                the next invoicing period.
                            </Trans>
                        </p>
                    )}
                    {!isTrialUpgrade && (
                        <p>
                            <Trans>
                                Are you sure you wish to change subscription to <span className="font-bold">{planName}</span>?
                            </Trans>
                        </p>
                    )}
                </>
                <Dialog.Footer>
                    <Group>
                        <Dialog.Close>
                            <Button>
                                <Trans>Go back</Trans>
                            </Button>
                        </Dialog.Close>
                        <Button onClick={selectPlan} appearance="primary" autoFocus>
                            {isTrialUpgrade && <Trans>Upgrade</Trans>}
                            {!isTrialUpgrade && <Trans>I am sure</Trans>}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Dialog.Content>
            <Dialog.Extra>
                <Trans>
                    You can view detailed information about all the new packages{' '}
                    <ExternalLink href="https://www.e-conomic.dk/prisoversigt">here</ExternalLink>
                </Trans>
            </Dialog.Extra>
        </Dialog>
    );
}
